import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { passwordResetService } from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) {
      setResult({
        type: 'error',
        message: 'Token de réinitialisation manquant'
      });
      setValidating(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setValidating(true);
      const response = await passwordResetService.validateToken(token);
      setTokenInfo(response);
    } catch (error) {
      console.error('Error validating token:', error);
      const errorMessage = error.response?.data?.detail || "Lien invalide ou expiré";
      setResult({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setValidating(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: "", color: "" };
    
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;

    if (strength <= 2) return { strength, text: "Faible", color: "text-red-500" };
    if (strength <= 3) return { strength, text: "Moyen", color: "text-orange-500" };
    return { strength, text: "Fort", color: "text-green-500" };
  };

  const validateForm = () => {
    if (!formData.new_password) {
      setResult({
        type: 'error',
        message: 'Veuillez saisir un nouveau mot de passe'
      });
      return false;
    }

    if (formData.new_password.length < 6) {
      setResult({
        type: 'error',
        message: 'Le mot de passe doit contenir au moins 6 caractères'
      });
      return false;
    }

    if (formData.new_password !== formData.confirm_password) {
      setResult({
        type: 'error',
        message: 'Les mots de passe ne correspondent pas'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setResult(null);
      
      await passwordResetService.resetPassword(
        token, 
        formData.new_password, 
        formData.confirm_password
      );
      
      setResult({
        type: 'success',
        message: 'Mot de passe réinitialisé avec succès ! Vous pouvez maintenant vous connecter.'
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Error resetting password:', error);
      
      const errorMessage = error.response?.data?.detail || "Erreur lors de la réinitialisation";
      setResult({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.new_password);

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Validation du lien en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenInfo && !result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Lien invalide</h2>
            <p className="text-gray-600 mb-6">Ce lien de réinitialisation est invalide ou a expiré.</p>
            <Button onClick={() => navigate('/forgot-password')} className="w-full">
              Demander un nouveau lien
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-gray-800 flex items-center gap-2 justify-center">
            <Lock className="w-6 h-6 text-green-600" />
            Nouveau mot de passe
          </CardTitle>
          {tokenInfo && (
            <CardDescription>
              Définir un nouveau mot de passe pour <strong>{tokenInfo.club_name}</strong>
            </CardDescription>
          )}
        </CardHeader>
        
        <CardContent className="space-y-6">
          {result && (
            <>
              <div className={`p-4 rounded-lg border ${
                result.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  {result.type === 'success' && <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />}
                  {result.type === 'error' && <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />}
                  
                  <p className={`text-sm font-medium ${
                    result.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {result.message}
                  </p>
                </div>
              </div>

              {result.type === 'success' && (
                <div className="text-center">
                  <Button onClick={() => navigate('/login')} className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Aller à la connexion
                  </Button>
                </div>
              )}
            </>
          )}

          {tokenInfo && !result && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="new_password" className="text-sm font-medium text-gray-700">
                  Nouveau mot de passe *
                </Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPasswords.new ? "text" : "password"}
                    value={formData.new_password}
                    onChange={(e) => handleInputChange('new_password', e.target.value)}
                    placeholder="Nouveau mot de passe"
                    className="w-full pr-10"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.new_password && (
                  <div className="flex items-center gap-2 text-xs">
                    <span>Force :</span>
                    <span className={passwordStrength.color}>
                      {passwordStrength.text}
                    </span>
                    <div className="flex-1 bg-gray-200 rounded-full h-1">
                      <div 
                        className={`h-1 rounded-full transition-all ${
                          passwordStrength.strength <= 2 ? 'bg-red-500' :
                          passwordStrength.strength <= 3 ? 'bg-orange-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-sm font-medium text-gray-700">
                  Confirmer le nouveau mot de passe *
                </Label>
                <div className="relative">
                  <Input
                    id="confirm_password"
                    type={showPasswords.confirm ? "text" : "password"}
                    value={formData.confirm_password}
                    onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                    placeholder="Confirmer le nouveau mot de passe"
                    className="w-full pr-10"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.confirm_password && formData.new_password && (
                  <div className="text-xs">
                    {formData.new_password === formData.confirm_password ? (
                      <span className="text-green-500">✓ Les mots de passe correspondent</span>
                    ) : (
                      <span className="text-red-500">✗ Les mots de passe ne correspondent pas</span>
                    )}
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Réinitialisation...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Réinitialiser le mot de passe
                  </>
                )}
              </Button>
            </form>
          )}

          {!result && (
            <div className="text-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;